import CustomDatePicker from '@/components/shared/CustomDatePicker';
import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import BillingStatementPrint from '@/components/finance/BillingStatementPrint';
import { useClientPOs } from '@/hooks/useClientPOs';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Search, ArrowDownRight, ArrowUpRight, User, Calendar, Building, Plus, X, Trash2, ChevronDown, Edit2, FileText, ExternalLink } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useManualReceivables } from '@/hooks/useManualReceivables';

import { useBankAccounts } from '@/hooks/useBankAccounts';

import SearchableSelect from '@/Components/hpp/SearchableSelect';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default function PayablesReceivables() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  });
  const [activePayablesFilter, setActivePayablesFilter] = useState('Semua');
  
  // State for expanded cards
  const [expandedCards, setExpandedCards] = useState({});
  const toggleCard = (id) => setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));

  // State for Export PDF
  const { accountsMap } = useBankAccounts();
  const [isExporting, setIsExporting] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [pendingPrintClient, setPendingPrintClient] = useState(null);
  const { data: settingsData } = useQuery({ queryKey: ['company-settings'], queryFn: () => base44.entities.CompanySettings.list() });

  const handleExportPDF = (client) => {
    setPendingPrintClient(client);
  };

  const executeExportPDF = async (client, withDetails) => {
    if (!client) return;
    setIsExporting(true);
    try {
      const invoices = [
        ...client.poList.map(item => ({
          id: item.id,
          date: item.rawPO?.date || item.rawPO?.tanggal || item.rawPO?.created_at || item.rawPO?.created_date,
          amount: item.sisa,
          products: withDetails ? (item.rawPO?.products || item.rawPO?.items || []) : []
        })),
        ...client.manualList.map(m => ({
          id: m.keterangan || 'Manual',
          date: m.tanggal_jatuh_tempo || m.tanggal,
          amount: m.sisa,
          products: []
        }))
      ].filter(inv => inv.amount > 0);

      // Extract bank from the newest invoice that has a paymentMethod
      let autoBankStr = '';
      const invoiceWithBank = [...client.poList]
        .sort((a, b) => new Date(b.date || b.rawPO?.created_at || 0) - new Date(a.date || a.rawPO?.created_at || 0))
        .find(item => item.rawPO?.paymentMethod);
        
      if (invoiceWithBank && invoiceWithBank.rawPO.paymentMethod) {
        const bankKey = invoiceWithBank.rawPO.paymentMethod;
        const acc = accountsMap?.[bankKey];
        if (acc) {
          autoBankStr = `${acc.nama_bank?.toUpperCase() || ''} - ${acc.name} (${acc.number || '-'})`.trim();
        }
      }

      const rawClientName = client.clientName;
      const cleanClientName = rawClientName.includes(' / ') ? rawClientName.split(' / ')[0] : rawClientName;

      setPrintData({
        clientName: cleanClientName,
        invoices,
        totalTagihan: client.sisa,
        bank: autoBankStr,
        companySettings: settingsData?.[0] || {}
      });
      setTimeout(() => {
        const oldTitle = document.title;
        document.title = `Rekap_Tagihan_${cleanClientName}`.replace(/\//g, '-');
        window.print();
        document.title = oldTitle;
        setIsExporting(false);
      }, 100);
      
    } catch (err) {
      console.error(err);
      toast.error('Gagal mengekspor PDF');
      setIsExporting(false);
    }
  };

  // Determine what to show based on portal and route
  const isFinance = location.pathname.startsWith('/office/finance');
  const isPurchasing = location.pathname.startsWith('/office/purchasing');
  const isMarketing = location.pathname.startsWith('/office/marketing');
  const isAnalytics = location.pathname.startsWith('/office/analytics');

  let showPayables = false;
  let showReceivables = false;

  if (isPurchasing) {
    showPayables = true;
  } else if (isMarketing) {
    showReceivables = true;
  } else if (isAnalytics) {
    showPayables = true;
    showReceivables = true;
  } else if (isFinance) {
    if (location.pathname.includes('/payables-receivables')) {
      showPayables = true;
      showReceivables = true;
    } else if (location.pathname.includes('/payables')) {
      showPayables = true;
    } else if (location.pathname.includes('/receivables')) {
      showReceivables = true;
    } else {
      showPayables = true;
      showReceivables = true;
    }
  }

  // 1. Fetch Hutang (Payables) from PurchasingRequests
  const { data: requests = [], isLoading: loadingReq } = useQuery({
    queryKey: ['purchasing-requests'],
    queryFn: () => base44.entities.PurchasingRequest.list('-created_date'),
    enabled: showPayables
  });

  // 2. Fetch Piutang (Receivables) from ClientPOs
  const { clientPOs = [], isLoading: loadingPO } = useClientPOs();
  
  // 3. Fetch Manual Receivables
  const { manualReceivables = [], isLoading: loadingManual, createManualReceivable, updateManualReceivable, deleteManualReceivable } = useManualReceivables();

  // Fetch for Hutang Modal
  const { data: bahanCairList = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });
  const { data: tutupList = [] } = useQuery({ queryKey: ['tutup'], queryFn: () => base44.entities.Tutup.list() });
  const { data: sprayList = [] } = useQuery({ queryKey: ['spray'], queryFn: () => base44.entities.Spray.list() });
  const { data: itemTambahanList = [] } = useQuery({ queryKey: ['item-tambahan'], queryFn: () => base44.entities.ItemTambahan.list() });
  const { data: fullsetListRaw = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });

  const { data: suppliers = [] } = useQuery({ 
    queryKey: ['supplier-database'], 
    queryFn: async () => {
      try {
        const res = await base44.entities.SupplierDatabase?.list();
        return res || [];
      } catch (err) {
        return [];
      }
    }
  });

  const allInventoryItems = useMemo(() => [
    ...bahanCairList.map(i => ({ ...i, _type: 'Bahan Cair' })),
    ...botolList.map(i => ({ ...i, _type: 'Botol' })),
    ...tutupList.map(i => ({ ...i, _type: 'Tutup' })),
    ...sprayList.map(i => ({ ...i, _type: 'Spray' })),
    ...fullsetListRaw.map(i => ({ ...i, _type: 'Fullset' })),
    ...itemTambahanList.map(i => ({ ...i, _type: 'Item Tambahan' }))
  ].sort((a, b) => (a.nama || '').localeCompare(b.nama || '')), [bahanCairList, botolList, tutupList, sprayList, fullsetListRaw, itemTambahanList]);

  // State for Manual Hutang Form
  const [showHutangModal, setShowHutangModal] = useState(false);
  const [hutangForm, setHutangForm] = useState({
    nama_sales: '',
    bank_tujuan: '',
    nomor_rekening: '',
    atas_nama: '',
    items: [{ nama_item: '', kategori_utama: '', kategori: '', sub_kategori_bahan: '', sub_kategori_tambahan: '', qty: '', harga: '', item_id_raw: '' }],
    tanggal_sampai: '',
    jatuh_tempo: ''
  });
  const [expandedHutangItems, setExpandedHutangItems] = useState({ 0: true });

  const toggleHutangItem = (idx) => {
    setExpandedHutangItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleAddHutangItem = () => {
    const newIdx = hutangForm.items.length;
    setHutangForm({
      ...hutangForm,
      items: [...hutangForm.items, { nama_item: '', kategori_utama: '', kategori: '', sub_kategori_bahan: '', sub_kategori_tambahan: '', qty: '', harga: '', item_id_raw: '' }]
    });
    setExpandedHutangItems(prev => ({ ...prev, [newIdx]: true }));
  };

  const handleRemoveHutangItem = (index) => {
    const newItems = [...hutangForm.items];
    newItems.splice(index, 1);
    setHutangForm({ ...hutangForm, items: newItems });
  };

  const handleHutangItemChange = (index, field, value) => {
    setHutangForm(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      return { ...prev, items: newItems };
    });
  };
  const [editHutangId, setEditHutangId] = useState(null);

  const createHutangMut = useMutation({
    mutationFn: (data) => base44.entities.PurchasingRequest.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchasing-requests'] });
      toast.success('Biaya hutang berhasil dicatat!');
      setShowHutangModal(false);
      setEditHutangId(null);
    }
  });

  const updateHutangMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.PurchasingRequest.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchasing-requests'] });
      toast.success('Hutang berhasil diupdate!');
      setShowHutangModal(false);
      setEditHutangId(null);
    }
  });

  const deleteHutangMut = useMutation({
    mutationFn: (id) => base44.entities.PurchasingRequest.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['purchasing-requests'] });
      toast.success('Hutang berhasil dihapus!');
      setShowHutangModal(false);
      setEditHutangId(null);
    }
  });

  const handleOpenEditHutang = (req) => {
    setEditHutangId(req.id);
    // Parse multi-items from hutang_items JSON or fallback to legacy single item
    let items = [];
    if (req.hutang_items) {
      try { items = JSON.parse(req.hutang_items); } catch(e) { items = []; }
    }
    if (items.length === 0) {
      items = [{ nama_item: req.nama_item || '', kategori_utama: '', kategori: req.kategori || '', sub_kategori_bahan: '', sub_kategori_tambahan: '', qty: req.qty_beli || '', harga: req.harga_unit || '', item_id_raw: '' }];
    }
    setHutangForm({
      nama_sales: req.nama_sales || '',
      bank_tujuan: req.bank_tujuan || '',
      nomor_rekening: req.nomor_rekening || '',
      atas_nama: req.atas_nama || '',
      items,
      tanggal_sampai: '',
      jatuh_tempo: req.tanggal_jatuh_tempo || '',
    });
    setExpandedHutangItems({ 0: true });
    setShowHutangModal(true);
  };

  const handleDeleteHutang = () => {
    if (!editHutangId) return;
    if (!confirm('Yakin ingin menghapus hutang ini?')) return;
    deleteHutangMut.mutate(editHutangId);
  };

  const handleCreateHutang = (e) => {
    e.preventDefault();
    if (!hutangForm.nama_sales || !hutangForm.jatuh_tempo) {
      toast.error('Harap lengkapi Nama Vendor dan Jatuh Tempo');
      return;
    }
    // Validate all items
    for (const item of hutangForm.items) {
      if (!item.nama_item || !item.qty || !item.harga) {
        toast.error('Harap lengkapi Nama Item, Qty, dan Harga pada semua item');
        return;
      }
    }
    // Calculate totals
    const grandTotal = hutangForm.items.reduce((sum, item) => sum + (Number(item.qty) * Number(item.harga)), 0);
    const firstItem = hutangForm.items[0];
    const itemNames = hutangForm.items.map(i => i.nama_item).join(', ');
    const payload = {
      nama_sales: hutangForm.nama_sales,
      nama_item: itemNames,
      kategori: firstItem.kategori || '',
      qty_beli: hutangForm.items.length === 1 ? Number(firstItem.qty) : hutangForm.items.reduce((s, i) => s + Number(i.qty), 0),
      harga_unit: hutangForm.items.length === 1 ? Number(firstItem.harga) : Math.round(grandTotal / hutangForm.items.reduce((s, i) => s + Number(i.qty), 0)),
      total_barang: grandTotal,
      total_biaya: grandTotal,
      metode_pembayaran: 'Tempo',
      status_pembayaran_tempo: 'Menunggu Jatuh Tempo',
      status: 'Menunggu Pembayaran',
      tanggal_jatuh_tempo: hutangForm.jatuh_tempo,
      bank_tujuan: hutangForm.bank_tujuan,
      nomor_rekening: hutangForm.nomor_rekening,
      atas_nama: hutangForm.atas_nama,
      hutang_items: JSON.stringify(hutangForm.items.map(i => ({ nama_item: i.nama_item, kategori: i.kategori, qty: Number(i.qty), harga: Number(i.harga) }))),
      catatan: hutangForm.tanggal_sampai ? `Tanggal Barang Sampai: ${hutangForm.tanggal_sampai}` : 'Hutang Manual'
    };
    if (editHutangId) {
      updateHutangMut.mutate({ id: editHutangId, data: payload });
    } else {
      createHutangMut.mutate(payload);
    }
  };

  // State for Manual Receivable Form
  const [showPiutangModal, setShowPiutangModal] = useState(false);
  const [editPiutangId, setEditPiutangId] = useState(null);
  const [piutangForm, setPiutangForm] = useState({
    namaBrand: '',
    items: [{ variant: '', qty: 1, hargaSatuan: '' }],
    tanggalSampai: '',
    jatuhTempo: ''
  });
  const [expandedVariants, setExpandedVariants] = useState({ 0: true });

  const [editPoTempoModal, setEditPoTempoModal] = useState(null);

  const toggleVariant = (idx) => {
    setExpandedVariants(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleAddItem = () => {
    const newIdx = piutangForm.items.length;
    setPiutangForm({
      ...piutangForm,
      items: [...piutangForm.items, { variant: '', qty: 1, hargaSatuan: '' }]
    });
    setExpandedVariants(prev => ({ ...prev, [newIdx]: true }));
  };

  const handleRemoveItem = (index) => {
    const newItems = [...piutangForm.items];
    newItems.splice(index, 1);
    setPiutangForm({ ...piutangForm, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...piutangForm.items];
    newItems[index][field] = value;
    setPiutangForm({ ...piutangForm, items: newItems });
  };

  const handleCreatePiutang = async (e) => {
    e.preventDefault();
    if (!piutangForm.namaBrand || piutangForm.items.length === 0) {
      toast.error('Harap isi field yang wajib');
      return;
    }
    
    // Validasi items
    for (const item of piutangForm.items) {
      if (!item.qty || !item.hargaSatuan) {
        toast.error('Harap isi Qty dan Harga Satuan pada semua varian');
        return;
      }
    }
    
    try {
      if (editPiutangId) {
        const item = piutangForm.items[0];
        await updateManualReceivable(editPiutangId, {
          namaBrand: piutangForm.namaBrand,
          variant: item.variant,
          qty: Number(item.qty),
          hargaSatuan: Number(item.hargaSatuan),
          total: Number(item.qty) * Number(item.hargaSatuan),
          tanggalSampai: piutangForm.tanggalSampai,
          jatuhTempo: piutangForm.jatuhTempo
        });
        toast.success('Piutang berhasil diupdate');
      } else {
        for (const item of piutangForm.items) {
          await createManualReceivable({
            namaBrand: piutangForm.namaBrand,
            variant: item.variant,
            qty: Number(item.qty),
            hargaSatuan: Number(item.hargaSatuan),
            total: Number(item.qty) * Number(item.hargaSatuan),
            tanggalSampai: piutangForm.tanggalSampai,
            jatuhTempo: piutangForm.jatuhTempo,
            status: 'Belum Lunas'
          });
        }
        toast.success('Piutang berhasil dicatat');
      }
      setShowPiutangModal(false);
      setEditPiutangId(null);
      setPiutangForm({
        namaBrand: '',
        items: [{ variant: '', qty: 1, hargaSatuan: '' }],
        tanggalSampai: '',
        jatuhTempo: ''
      });
    } catch (e) {
      toast.error(editPiutangId ? 'Gagal mengupdate piutang' : 'Gagal mencatat piutang');
    }
  };

  const handleOpenAddPiutang = () => {
    setEditPiutangId(null);
    setPiutangForm({
      namaBrand: '',
      items: [{ variant: '', qty: 1, hargaSatuan: '' }],
      tanggalSampai: '',
      jatuhTempo: ''
    });
    setExpandedVariants({ 0: true });
    setShowPiutangModal(true);
  };

  const handleOpenEditPiutang = (mr) => {
    setEditPiutangId(mr.id);
    setPiutangForm({
      namaBrand: mr.namaBrand || '',
      items: [{ variant: mr.variant || '', qty: mr.qty || 1, hargaSatuan: mr.hargaSatuan || 0 }],
      tanggalSampai: mr.tanggalSampai || '',
      jatuhTempo: mr.jatuhTempo || ''
    });
    setExpandedVariants({ 0: true });
    setShowPiutangModal(true);
  };


  // Process Payables (Hutang)
  const payables = useMemo(() => {
    if (!showPayables) return [];
    
    const supplierBalances = {};
    
    requests.forEach(req => {
      const isTempo = req.metode_pembayaran === 'Tempo' && req.status_pembayaran_tempo === 'Menunggu Jatuh Tempo';
      const isDicicil = req.status === 'Dicicil';
      
      if (isTempo || isDicicil) {
        const baseTotal = req.custom_total_biaya !== undefined ? req.custom_total_biaya : (req.total_biaya || 0);
        const sisaTagihan = baseTotal - (Number(req.jumlah_terbayar) || 0);
        if (sisaTagihan > 0) {
          const supplierName = req.nama_sales || 'Vendor Tidak Diketahui';
          if (!supplierBalances[supplierName]) {
            supplierBalances[supplierName] = { supplierName, sisa: 0, count: 0, hutangList: [] };
          }
          supplierBalances[supplierName].sisa += sisaTagihan;
          supplierBalances[supplierName].count += 1;
          supplierBalances[supplierName].hutangList.push(req);
        }
      }
    });

    return Object.values(supplierBalances).sort((a, b) => b.sisa - a.sisa);
  }, [requests, showPayables]);

  const totalPayables = useMemo(() => {
    return payables.reduce((sum, supplier) => sum + supplier.sisa, 0);
  }, [payables]);

  // Process Receivables (Piutang)
  const receivables = useMemo(() => {
    if (!showReceivables) return [];
    
    // Group unpaid balances by Client/Brand
    const clientBalances = {};
    
    clientPOs.forEach(po => {
      if (po.poMode === 'sph') return;
      // Hapus batasan is_tempo agar semua invoice yang belum lunas masuk Piutang
      // if (!po.is_tempo && po.statusBayar !== 'TEMPO' && !po.rawRecord?.is_tempo) {
      //   return;
      // }

      // Hitung Grand Total (Mendukung struktur products & items baru)
      let rawProducts = po.products || po.items || [];
      if (typeof rawProducts === 'string') try { rawProducts = JSON.parse(rawProducts); } catch(e) { rawProducts = []; }
      const prodJual = rawProducts.reduce((s, p) => s + (p.total_harga_jual || p.subtotal || p.hargaTotal || 0), 0);
      
      let rawLegalitas = po.legalitas || [];
      if (typeof rawLegalitas === 'string') try { rawLegalitas = JSON.parse(rawLegalitas); } catch(e) { rawLegalitas = []; }
      const legJual = rawLegalitas.reduce((s, l) => s + ((l.harga_jual || 0) * (l.qty_variant || 1)), 0);

      const ppnMode = po.ppn_mode || po.rawRecord?.ppn_mode;
      const ppnPercent = parseFloat(po.ppn_percentage || po.rawRecord?.ppn_percentage) || 0;
      const nominalPPN = ppnMode === 'exclude' ? ((prodJual + legJual) * ppnPercent / 100) : 0;

      const grandTotal = prodJual + legJual + (po.shippingCost || 0) + nominalPPN;
      
      let terbayar = 0;
      if (po.riwayatPembayaran && po.riwayatPembayaran.length > 0) {
        const validPayments = po.riwayatPembayaran.filter(p => p.status_konfirmasi !== 'Menunggu Konfirmasi Finance');
        terbayar = validPayments.reduce((s, r) => s + (Number(r.jumlah) || 0), 0);
      }

      const sisa = Math.max(0, Math.round(grandTotal - terbayar));
      
      const isTempo = po.is_tempo || po.statusBayar === 'TEMPO' || po.paymentTerms === 'tempo' || po.rawRecord?.is_tempo || po.rawRecord?.paymentTerms === 'tempo';
      const hasPayment = terbayar > 0;
      
      // Abaikan selisih desimal kecil di bawah Rp 1.000 (seperti ORKAI Rp 66) agar piutang riil tetap utuh
      if (sisa >= 1000 && (isTempo || hasPayment)) {
        let rawClientName = po.customerName || po.brand_name || 'Klien Tidak Diketahui';
        
        // Coba pertahankan format 'Brand / PIC' tapi UPPERCASE brand-nya dan normalisasi PIC
        let clientKey = rawClientName.toUpperCase();
        if (rawClientName.includes(' / ')) {
          const parts = rawClientName.split(' / ');
          const brand = parts[0].trim().toUpperCase();
          let pic = parts[1].trim();
          if (pic.toLowerCase() === 'adnan') pic = 'Adnan Hasan';
          clientKey = `${brand} / ${pic}`;
        }
        
        if (!clientBalances[clientKey]) {
          clientBalances[clientKey] = { clientName: clientKey, sisa: 0, count: 0, poList: [], manualList: [] };
        }
        clientBalances[clientKey].sisa += sisa;
        clientBalances[clientKey].count += 1;
        clientBalances[clientKey].poList.push({ id: po.id, sisa, rawPO: po });
      }
    });

    // Merge with Manual Receivables
    manualReceivables.forEach(mr => {
      if (mr.status !== 'Lunas') {
        let rawClientName = mr.namaBrand || 'Klien Tidak Diketahui';
        
        // Sama dengan atas, jadikan UPPERCASE untuk key dan display & normalisasi PIC
        let clientKey = rawClientName.toUpperCase();
        if (rawClientName.includes(' / ')) {
          const parts = rawClientName.split(' / ');
          const brand = parts[0].trim().toUpperCase();
          let pic = parts[1].trim();
          if (pic.toLowerCase() === 'adnan') pic = 'Adnan Hasan';
          clientKey = `${brand} / ${pic}`;
        }
        
        if (!clientBalances[clientKey]) {
          clientBalances[clientKey] = { clientName: clientKey, sisa: 0, count: 0, poList: [], manualList: [] };
        }
        const mrTotal = mr.total || 0;
        clientBalances[clientKey].sisa += mrTotal;
        clientBalances[clientKey].count += 1;
        clientBalances[clientKey].manualList.push({ ...mr, sisa: mrTotal });
      }
    });

    return Object.values(clientBalances).sort((a, b) => b.sisa - a.sisa);
  }, [clientPOs, manualReceivables, showReceivables]);

  const totalReceivables = useMemo(() => {
    return receivables.reduce((sum, client) => sum + client.sisa, 0);
  }, [receivables]);

  const isLoading = (showPayables && loadingReq) || (showReceivables && (loadingPO || loadingManual));

  // Filter logic
  const filteredPayables = payables.map(p => {
    if (!searchTerm) return p;
    if (p.supplierName.toLowerCase().includes(searchTerm.toLowerCase())) return p;
    
    const matchedPRs = p.hutangList.filter(req => String(req.id).toLowerCase().includes(searchTerm.toLowerCase()));
    if (matchedPRs.length > 0) {
      return { 
        ...p, 
        hutangList: matchedPRs, 
        sisa: matchedPRs.reduce((sum, req) => sum + (req.total_biaya || 0), 0), 
        count: matchedPRs.length 
      };
    }
    return null;
  }).filter(Boolean);

  const finallyFilteredPayables = filteredPayables.filter(p => {
    if (activePayablesFilter === 'Semua') return true;
    const hasFaktur = p.hutangList.some(req => req.ada_faktur_pajak === 'Ya');
    if (activePayablesFilter === 'SDE') return hasFaktur;
    if (activePayablesFilter === 'ASB') return !hasFaktur;
    return true;
  });

  
  const filteredReceivables = receivables.map(r => {
    if (!searchTerm) return r;
    if (r.clientName.toLowerCase().includes(searchTerm.toLowerCase())) return r;

    const matchedPOs = r.poList.filter(po => String(po.id).toLowerCase().includes(searchTerm.toLowerCase()));
    const matchedManuals = r.manualList.filter(mr => String(mr.id).toLowerCase().includes(searchTerm.toLowerCase()));
    
    if (matchedPOs.length > 0 || matchedManuals.length > 0) {
      const newSisa = matchedPOs.reduce((sum, po) => sum + po.sisa, 0) + matchedManuals.reduce((sum, mr) => sum + mr.sisa, 0);
      return { 
        ...r, 
        poList: matchedPOs, 
        manualList: matchedManuals, 
        sisa: newSisa, 
        count: matchedPOs.length + matchedManuals.length 
      };
    }
    return null;
  }).filter(Boolean);

  // Auto expand logic
  React.useEffect(() => {
    if (searchTerm) {
      const newExpanded = { ...expandedCards };
      let changed = false;
      filteredPayables.forEach((p, idx) => {
        if (p.hutangList.some(req => String(req.id).includes(searchTerm))) {
          newExpanded[`payable-${idx}`] = true;
          changed = true;
        }
      });
      filteredReceivables.forEach((r, idx) => {
        if (r.poList.some(po => String(po.id).includes(searchTerm)) || r.manualList.some(mr => String(mr.id).includes(searchTerm))) {
          newExpanded[`rec-${idx}`] = true;
          changed = true;
        }
      });
      if (changed) setExpandedCards(newExpanded);
    }
  }, [searchTerm, payables, receivables]);
return (
    <div className="pb-16">
      <div className="space-y-6 print:hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black tracking-tight text-slate-800">
            {isAnalytics || location.pathname.includes('/payables-receivables') ? 'Hutang & Piutang' 
             : location.pathname.includes('/payables') ? 'Hutang Perusahaan' 
             : 'Piutang Klien'}
          </h1>
          <p className="text-[9px] md:text-xs text-slate-500 mt-0.5 md:mt-1">
            {isAnalytics || location.pathname.includes('/payables-receivables')
              ? 'Laporan ringkasan tagihan yang harus dibayar (Hutang) dan tagihan yang belum dilunasi klien (Piutang).' 
              : location.pathname.includes('/payables')
              ? 'Daftar pengajuan pembelian dengan metode Tempo yang belum dilunasi.'
              : 'Daftar invoice klien yang belum dilunasi sepenuhnya.'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
            <Input 
              placeholder="Cari nama..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 border-slate-200"
            />
          </div>
          {showPayables && (
            <Button 
              onClick={() => {
                setEditHutangId(null);
                setHutangForm({
                  nama_sales: '',
                  items: [{ nama_item: '', kategori_utama: '', kategori: '', sub_kategori_bahan: '', sub_kategori_tambahan: '', qty: '', harga: '', item_id_raw: '' }],
                  tanggal_sampai: '',
                  jatuh_tempo: ''
                });
                setExpandedHutangItems({ 0: true });
                setShowHutangModal(true);
              }}
              className="h-10 bg-rose-600 hover:bg-rose-700 text-white font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" /> Tambah Hutang
            </Button>
          )}
          {showReceivables && (
            <Button 
              onClick={handleOpenAddPiutang}
              className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" /> Tambah Piutang
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <div className="w-8 h-8 border-4 border-[#8C6208] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          {/* Kolom Hutang (Payables) */}
          {showPayables && (
            <div className={`space-y-4 ${!showReceivables ? 'md:col-span-2' : ''}`}>
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 md:p-5 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2 text-rose-700">
                    <ArrowUpRight className="w-4 h-4 md:w-5 md:h-5" />
                    <h3 className="font-black text-xs md:text-sm tracking-wide">TOTAL HUTANG</h3>
                  </div>
                  <Badge className="bg-rose-100 text-rose-700 border-rose-200 shadow-none text-[8px] md:text-xs">Kewajiban Keluar</Badge>
                </div>
                <p className="text-2xl md:text-3xl font-black text-rose-600">Rp {fmt(totalPayables)}</p>
              </div>

              <div className="space-y-4">
                {finallyFilteredPayables.length === 0 ? (
                  <div className="text-center py-10 bg-slate-50 border border-slate-200 border-dashed rounded-xl">
                    <p className="text-sm font-bold text-slate-400">Tidak ada hutang tercatat.</p>
                  </div>
                ) : (
                  finallyFilteredPayables.map((supplier, idx) => {
                    const isExpanded = expandedCards[`payable-${idx}`];
                    return (
                    <Card key={`payable-${idx}`} className="border-0 shadow-sm overflow-hidden bg-white hover:shadow-md transition-shadow">
                      <div 
                        className="flex justify-between items-center p-4 md:p-5 cursor-pointer border-l-4 border-l-rose-500 group"
                        onClick={() => toggleCard(`payable-${idx}`)}
                      >
                        <div className="flex items-center gap-2.5 md:gap-4">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-rose-50 flex items-center justify-center group-hover:bg-rose-100 transition-colors">
                            <Building className="w-4 h-4 md:w-5 md:h-5 text-rose-600" />
                          </div>
                          <div>
                            <h4 className="font-black text-slate-800 text-sm md:text-lg group-hover:text-rose-700 transition-colors">
                              {supplier.supplierName}

                            {(() => {
                              const sdeCount = supplier.hutangList.filter(r => r.ada_faktur_pajak === 'Ya').length;
                              const asbCount = supplier.hutangList.length - sdeCount;
                              return (
                                <span className="ml-2 inline-flex items-center gap-1.5 align-middle">
                                  {sdeCount > 0 && (
                                    <span className="inline-flex items-center bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-md text-[9px] font-bold border border-emerald-200 uppercase tracking-wider">SDE : {sdeCount} PO</span>
                                  )}
                                  {asbCount > 0 && (
                                    <span className="inline-flex items-center bg-slate-100 text-slate-500 px-2 py-0.5 rounded-md text-[9px] font-bold border border-slate-200 uppercase tracking-wider">ASB : {asbCount} PO</span>
                                  )}
                                </span>
                              );
                            })()}
  
                            </h4>
                            <p className="text-[9px] md:text-xs font-bold text-slate-500 mt-0.5">{supplier.count} Tagihan Hutang</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4">
                          <div className="text-right">
                            <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Sisa Hutang</p>
                            <p className="font-black text-rose-600 text-sm md:text-xl">Rp {fmt(supplier.sisa)}</p>
                          </div>
                          <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="bg-slate-50/50 border-t border-slate-100 p-5 space-y-3">
                          {supplier.hutangList.map((req, reqIdx) => {
                            let parsedItems = [];
                            if (req.hutang_items) { try { parsedItems = JSON.parse(req.hutang_items); } catch(e) {} }
                            
                            return (
                              <div key={req.id} className="bg-white border border-slate-200 rounded-xl p-4 flex justify-between items-start hover:border-rose-300 transition-colors shadow-sm">
                                <div>
                                  {parsedItems.length > 1 ? (
                                    <div className="space-y-1.5">
                                      {parsedItems.map((pi, piIdx) => (
                                        <p key={piIdx} className="text-xs font-semibold text-slate-600">
                                          <span className="font-black text-slate-800">{pi.nama_item}</span> — {pi.qty} pcs × Rp {fmt(pi.harga)} = <span className="font-bold text-rose-600">Rp {fmt(pi.qty * pi.harga)}</span>
                                        </p>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-xs font-black text-slate-800 mb-1">{req.nama_item}</p>
                                  )}
                                  <div className="flex items-center flex-wrap gap-3 mt-2 text-[11px] font-bold">
                                    {req.ada_faktur_pajak === 'Ya' ? (
                                      <span className="inline-flex items-center bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[9px] font-bold border border-emerald-200 uppercase tracking-wider">SDE (BCA)</span>
                                    ) : (
                                      <span className="inline-flex items-center bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded text-[9px] font-bold border border-slate-200 uppercase tracking-wider">ASB (BCA)</span>
                                    )}
                                    <span className="flex items-center gap-1 text-slate-500">
                                      <Calendar className="w-3.5 h-3.5" /> 
                                      Jatuh Tempo: <span className="text-rose-600">{req.tanggal_jatuh_tempo || '-'}</span>
                                    </span>
                                    {parsedItems.length <= 1 && req.qty_beli && (
                                      <span className="text-slate-400">
                                        {req.qty_beli} × Rp {fmt(req.harga_unit)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="text-right flex flex-col items-end gap-2">
                                  {Number(req.jumlah_terbayar) > 0 && (
                                    <span className="text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                                      Dicicil
                                    </span>
                                  )}
                                  <p className="font-black text-rose-600 bg-rose-50 px-2 py-1 rounded-lg">
                                    Rp {fmt((req.custom_total_biaya !== undefined ? req.custom_total_biaya : (req.total_biaya || 0)) - (Number(req.jumlah_terbayar) || 0))}
                                  </p>
                                  <Button variant="ghost" size="sm" onClick={() => handleOpenEditHutang(req)} className="h-7 text-[10px] text-slate-500 hover:text-[#8C6208] hover:bg-[#F9F5EC] border border-transparent hover:border-[#E6D5B8]">
                                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>
                  );})
                )}
              </div>
            </div>
          )}

          {/* Kolom Piutang (Receivables) */}
          {showReceivables && (
            <div className={`space-y-4 ${!showPayables ? 'md:col-span-2' : ''}`}>
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 md:p-5 shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2 text-emerald-700">
                    <ArrowDownRight className="w-4 h-4 md:w-5 md:h-5" />
                    <h3 className="font-black text-xs md:text-sm tracking-wide">TOTAL PIUTANG</h3>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 shadow-none text-[8px] md:text-xs">Pemasukan Tertunda</Badge>
                </div>
                <p className="text-2xl md:text-3xl font-black text-emerald-600">Rp {fmt(totalReceivables)}</p>
              </div>

              <div className="space-y-4">
                {filteredReceivables.length === 0 ? (
                  <div className="text-center py-10 bg-slate-50 border border-slate-200 border-dashed rounded-xl">
                    <p className="text-sm font-bold text-slate-400">Tidak ada piutang tercatat.</p>
                  </div>
                ) : (
                  filteredReceivables.map((client, idx) => {
                    const isExpanded = expandedCards[`receivable-${idx}`];
                    return (
                    <Card key={`receivable-${idx}`} className="border-0 shadow-sm overflow-hidden bg-white hover:shadow-md transition-shadow">
                      <div 
                        className="flex justify-between items-center p-4 md:p-5 cursor-pointer border-l-4 border-l-emerald-500 group"
                        onClick={() => toggleCard(`receivable-${idx}`)}
                      >
                        <div className="flex items-center gap-2.5 md:gap-4">
                          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                            <User className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-black text-slate-800 text-sm md:text-lg group-hover:text-emerald-700 transition-colors">
                              {client.clientName}
                            </h4>
                            <p className="text-[9px] md:text-xs font-bold text-slate-500 mt-0.5">{client.count} Invoice Belum Lunas</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 md:gap-4">
                          <div className="text-right">
                            <p className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">Total Sisa Piutang</p>
                            <p className="font-black text-emerald-600 text-sm md:text-xl">Rp {fmt(client.sisa)}</p>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={(e) => { e.stopPropagation(); handleExportPDF(client); }}
                            className="h-8 w-8 text-emerald-600 hover:text-white hover:bg-emerald-600 rounded-lg transition-colors border border-transparent hover:border-emerald-200 bg-emerald-50 mr-1"
                            title="Ekspor PDF Penagihan"
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="bg-slate-50/50 border-t border-slate-100 p-5 space-y-3">
                          {client.poList.map(po => {
                            const raw = po.rawPO || {};
                            const poStatus = (raw.status || raw.po_status || '').toLowerCase();
                            const isShipped = ['dikirim', 'shipped', 'terkirim', 'selesai_dikirim', 'delivered'].includes(poStatus) || !!raw.tanggal_barang_sampai || !!raw.tanggal_dikirim || !!raw.tanggal_pengiriman;
                            const isReadyToShip = poStatus === 'siap_kirim';

                            const tglDikirim = raw.tanggal_barang_sampai || raw.tanggal_dikirim || raw.tanggal_pengiriman || raw.shipped_at || (isShipped ? (raw.updated_date ? format(new Date(raw.updated_date), 'dd MMM yyyy') : 'Selesai Dikirim') : null);

                            return (
                              <div key={po.id} className={`bg-white border rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 transition-colors shadow-sm ${isShipped ? 'border-emerald-300 bg-emerald-50/20' : 'border-slate-200 hover:border-emerald-300'}`}>
                                <div>
                                  <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <p 
                                      className="text-xs font-black text-[#8C6208] cursor-pointer hover:underline flex items-center gap-1.5 transition-all"
                                      onClick={() => navigate(isFinance ? `/office/finance/invoices?search=${po.id}` : `/office/marketing/invoices?search=${po.id}`)}
                                      title="Buka detail invoice ini"
                                    >
                                      ID Transaksi: {po.id} <ExternalLink className="w-3 h-3 text-slate-400" />
                                    </p>

                                    {/* Status Badge Pengiriman */}
                                    {isShipped ? (
                                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] font-bold px-2 py-0.5 shadow-none">
                                        🚚 Pengiriman Selesai
                                      </Badge>
                                    ) : isReadyToShip ? (
                                      <Badge className="bg-blue-100 text-blue-800 border-blue-300 text-[10px] font-bold px-2 py-0.5 shadow-none">
                                        📦 Siap Kirim
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-slate-100 text-slate-700 border-slate-300 text-[10px] font-bold px-2 py-0.5 shadow-none">
                                        ⚙️ Dalam Produksi
                                      </Badge>
                                    )}
                                  </div>

                                  <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] font-bold">
                                    <span className="flex items-center gap-1 text-slate-500">
                                      <Calendar className="w-3.5 h-3.5 text-slate-400" /> 
                                      Jatuh Tempo: <span className="text-emerald-700 font-extrabold">
                                        {raw.tanggal_jatuh_tempo || raw.tglJatuhTempo || raw.tempoDate || raw.due_date || raw.dueDate || '-'}
                                      </span>
                                    </span>

                                    {tglDikirim && (
                                      <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200">
                                        <span>Dikirim / Selesai:</span> <strong>{tglDikirim}</strong>
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="text-right flex flex-col items-end gap-1 shrink-0">
                                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">Sisa Piutang</p>
                                  <p className="font-black text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg text-sm md:text-base border border-emerald-100">Rp {fmt(po.sisa)}</p>
                                </div>
                              </div>
                            );
                          })}
                          
                          {client.manualList?.map(mr => (
                            <div key={mr.id} className="bg-white border border-emerald-200/60 rounded-xl p-4 flex justify-between items-start hover:border-emerald-300 transition-colors shadow-sm relative overflow-hidden">
                              <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-50 -mr-8 -mt-8 rounded-full opacity-50 pointer-events-none" />
                              <div className="relative z-10">
                                <p className="text-xs font-black text-slate-800 mb-1">{mr.variant || 'Piutang Manual'}</p>
                                <div className="flex items-center gap-3 mt-2 text-[11px] font-bold">
                                  <span className="flex items-center gap-1 text-slate-500">
                                    <Calendar className="w-3.5 h-3.5" /> 
                                    Jatuh Tempo: <span className="text-emerald-600">{mr.jatuhTempo || '-'}</span>
                                  </span>
                                  {mr.tanggalSampai && (
                                    <span className="text-slate-400">Sampai: {mr.tanggalSampai}</span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right flex flex-col items-end gap-2 relative z-10">
                                <p className="font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">Rp {fmt(mr.sisa)}</p>
                                <Button variant="ghost" size="sm" onClick={() => handleOpenEditPiutang(mr)} className="h-7 text-[10px] text-slate-500 hover:text-[#8C6208] hover:bg-[#F9F5EC] border border-transparent hover:border-[#E6D5B8]">
                                  <Edit2 className="w-3 h-3 mr-1" /> Edit
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  );})
                )}
              </div>
            </div>
          )}

        </div>
      )}

      {/* MODAL TAMBAH PIUTANG - HMR VERIFIED */}
      {showPiutangModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 ">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
              <h2 className="font-bold text-slate-800">{editPiutangId ? 'Edit Piutang Manual' : 'Tambah Piutang Manual'}</h2>
              <div className="flex items-center gap-2">
                {editPiutangId && (
                  <Button variant="ghost" size="sm" onClick={handleDeletePiutang} className="text-rose-500 hover:bg-rose-50 hover:text-rose-600 h-8 px-2">
                    <Trash2 className="w-4 h-4 mr-1" /> Hapus
                  </Button>
                )}
                <button 
                  onClick={() => setShowPiutangModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreatePiutang} className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="space-y-1">
                <Label className="text-xs font-bold text-slate-600">Nama Brand / Klien <span className="text-rose-500">*</span></Label>
                <Input 
                  value={piutangForm.namaBrand} 
                  onChange={e => setPiutangForm({ ...piutangForm, namaBrand: e.target.value })} 
                  placeholder="Contoh: MS Glow"
                  required
                />
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-bold text-slate-600">Daftar Variant</Label>
                  {!editPiutangId && (
                    <Button type="button" variant="outline" size="sm" onClick={handleAddItem} className="h-7 text-[10px]">
                      <Plus className="w-3 h-3 mr-1" /> Tambah Variant
                    </Button>
                  )}
                </div>
                
                <div className="max-h-[500px] overflow-y-auto pr-1 space-y-2">
                  {piutangForm.items.map((item, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
                      {/* Accordion Header */}
                      <div 
                        className="flex items-center justify-between px-3 py-2.5 bg-amber-50/60 border-b border-amber-100 cursor-pointer select-none hover:bg-amber-50 transition-colors"
                        onClick={() => toggleVariant(idx)}
                      >
                        <div className="flex items-center gap-2.5">
                          <span className="w-6 h-6 rounded-full bg-[#8C6208] text-white text-[10px] font-bold flex items-center justify-center shadow-sm">
                            {idx + 1}
                          </span>
                          <span className="text-xs font-semibold text-slate-700">
                            {item.variant || `Variant ${idx + 1}`}
                          </span>
                          {item.qty && item.hargaSatuan && (
                            <span className="text-[10px] text-slate-400 ml-1">
                              — Rp {fmt(Number(item.qty) * Number(item.hargaSatuan))}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {piutangForm.items.length > 1 && !editPiutangId && (
                            <button 
                              type="button" 
                              onClick={(e) => { e.stopPropagation(); handleRemoveItem(idx); }}
                              className="p-1 text-slate-400 hover:text-rose-500 transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${expandedVariants[idx] ? 'rotate-180' : ''}`} />
                        </div>
                      </div>

                      {/* Accordion Content */}
                      {expandedVariants[idx] && (
                        <div className="p-3 bg-white space-y-3">
                          <div className="space-y-1">
                            <Label className="text-[10px] font-bold text-slate-500">Variant Produk</Label>
                            <Input 
                              value={item.variant} 
                              onChange={e => handleItemChange(idx, 'variant', e.target.value)} 
                              placeholder="Contoh: Serum Luminous"
                              className="h-8 text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-[10px] font-bold text-slate-500">Qty <span className="text-rose-500">*</span></Label>
                              <Input 
                                type="number"
                                value={item.qty} 
                                onChange={e => handleItemChange(idx, 'qty', e.target.value)} 
                                min="1"
                                className="h-8 text-sm"
                                required
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-[10px] font-bold text-slate-500">Harga Satuan <span className="text-rose-500">*</span></Label>
                              <Input 
                                type="number"
                                value={item.hargaSatuan} 
                                onChange={e => handleItemChange(idx, 'hargaSatuan', e.target.value)} 
                                placeholder="0"
                                className="h-8 text-sm"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500">Total Tagihan Keseluruhan</span>
                <span className="font-black text-emerald-600">
                  Rp {fmt(piutangForm.items.reduce((sum, item) => sum + ((Number(item.qty) || 0) * (Number(item.hargaSatuan) || 0)), 0))}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-slate-500">Tgl Barang Sampai</Label>
                  <CustomDatePicker
                    value={piutangForm.tanggalSampai} 
                    onChange={date => setPiutangForm({ ...piutangForm, tanggalSampai: date })} 
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-slate-500">Jatuh Tempo</Label>
                  <CustomDatePicker
                    value={piutangForm.jatuhTempo} 
                    onChange={date => setPiutangForm({ ...piutangForm, jatuhTempo: date })} 
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="w-full" onClick={() => setShowPiutangModal(false)}>
                  Batal
                </Button>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                  Simpan Piutang
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL TAMBAH HUTANG */}
      {showHutangModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 ">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
              <h2 className="font-bold text-slate-800">{editHutangId ? 'Edit Biaya Hutang' : 'Tambah Biaya Hutang'}</h2>
              <div className="flex items-center gap-2">
                {editHutangId && (
                  <Button type="button" variant="ghost" size="sm" onClick={handleDeleteHutang} className="text-rose-500 hover:bg-rose-50 hover:text-rose-600 h-8 px-2">
                    <Trash2 className="w-4 h-4 mr-1" /> Hapus
                  </Button>
                )}
                <button 
                  type="button"
                  onClick={() => setShowHutangModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <form onSubmit={handleCreateHutang} className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs font-bold text-slate-600">Pilih dari Database Supplier (Opsional)</Label>
                  <SearchableSelect
                    options={[...suppliers].sort((a, b) => (a.nama_sales || '').localeCompare(b.nama_sales || ''))}
                    onChange={(id) => {
                      if (!id) return;
                      const sup = suppliers.find(s => String(s.id) === String(id));
                      if (sup) setHutangForm(f => ({ 
                        ...f, 
                        nama_sales: sup.nama_sales,
                        bank_tujuan: sup.bank || '',
                        nomor_rekening: sup.no_rekening || '',
                        atas_nama: sup.atas_nama || ''
                      }));
                    }}
                    placeholder="-- Pilih untuk auto-fill vendor --"
                    displayKey="nama_sales"
                    valueKey="id"
                    renderOption={(opt) => (
                      <div>
                        <div className="font-semibold text-sm text-[#302621]">{opt.nama_sales}</div>
                      </div>
                    )}
                  />
                </div>

                <div className="col-span-2 space-y-1">
                  <Label className="text-xs font-bold text-slate-600">Nama Vendor / Supplier <span className="text-rose-500">*</span></Label>
                  <Input 
                    value={hutangForm.nama_sales} 
                    onChange={e => setHutangForm({ ...hutangForm, nama_sales: e.target.value })} 
                    placeholder="Contoh: PT. Bintang Jaya"
                    required
                  />
                </div>
                
                <div className="col-span-2 grid grid-cols-3 gap-3 bg-[#F9F5EC]/40 p-3 rounded-xl border border-[#E6D5B8]/50">
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-[#8C6208]">Bank Tujuan</Label>
                    <Input 
                      value={hutangForm.bank_tujuan} 
                      onChange={e => setHutangForm({ ...hutangForm, bank_tujuan: e.target.value })} 
                      placeholder="Contoh: BCA"
                      className="bg-white border-[#E6D5B8]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-[#8C6208]">No. Rekening</Label>
                    <Input 
                      value={hutangForm.nomor_rekening} 
                      onChange={e => setHutangForm({ ...hutangForm, nomor_rekening: e.target.value })} 
                      placeholder="Nomor rekening"
                      className="bg-white border-[#E6D5B8]"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-bold text-[#8C6208]">Atas Nama</Label>
                    <Input 
                      value={hutangForm.atas_nama} 
                      onChange={e => setHutangForm({ ...hutangForm, atas_nama: e.target.value })} 
                      placeholder="Atas nama"
                      className="bg-white border-[#E6D5B8]"
                    />
                  </div>
                </div>
              </div>

              {/* Multi-item accordion */}
              <div className="space-y-2">
                <Label className="text-xs font-bold text-slate-600">Daftar Item Hutang</Label>
                {hutangForm.items.map((htItem, idx) => {
                  const isOpen = expandedHutangItems[idx];
                  const itemTotal = (Number(htItem.qty) || 0) * (Number(htItem.harga) || 0);
                  return (
                    <div key={idx} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                      {/* Accordion Header */}
                      <button
                        type="button"
                        onClick={() => toggleHutangItem(idx)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-amber-50/70 hover:bg-amber-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-[#D4AF37] text-white text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                          <span className="text-sm font-medium text-slate-700 truncate max-w-[280px]">
                            {htItem.nama_item || `Item ${idx + 1}`}
                            {itemTotal > 0 && <span className="text-xs text-slate-400 ml-2">— Rp {fmt(itemTotal)}</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {hutangForm.items.length > 1 && (
                            <span
                              onClick={(e) => { e.stopPropagation(); handleRemoveHutangItem(idx); }}
                              className="p-1 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-full transition-colors"
                            >
                              <X className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </div>
                      </button>

                      {/* Accordion Body */}
                      {isOpen && (
                        <div className="px-4 py-3 space-y-3 border-t border-slate-100">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 md:col-span-1 space-y-1">
                              <Label className="text-xs font-bold text-slate-600">Kategori Utama</Label>
                              <Select 
                                value={htItem.kategori_utama || ''}
                                onValueChange={(val) => {
                                  handleHutangItemChange(idx, 'kategori_utama', val);
                                  handleHutangItemChange(idx, 'kategori', '');
                                  handleHutangItemChange(idx, 'sub_kategori_bahan', '');
                                  handleHutangItemChange(idx, 'sub_kategori_tambahan', '');
                                  handleHutangItemChange(idx, 'item_id_raw', '');
                                }}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue placeholder="-- Pilih Kategori --" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Packaging">Packaging</SelectItem>
                                  <SelectItem value="Bahan Cair">Bahan Cair</SelectItem>
                                  <SelectItem value="Item Tambahan">Item Tambahan</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div className="col-span-2 md:col-span-1 space-y-1">
                              <Label className="text-xs font-bold text-slate-600">Sub Kategori</Label>
                              {htItem.kategori_utama === 'Packaging' ? (
                                <Select 
                                  value={htItem.kategori || ''}
                                  onValueChange={(val) => handleHutangItemChange(idx, 'kategori', val)}
                                >
                                  <SelectTrigger className="h-9"><SelectValue placeholder="-- Sub Kategori --" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Fullset">Fullset</SelectItem>
                                    <SelectItem value="Botol">Botol</SelectItem>
                                    <SelectItem value="Tutup">Tutup</SelectItem>
                                    <SelectItem value="Spray">Spray</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : htItem.kategori_utama === 'Bahan Cair' ? (
                                <Select 
                                  value={htItem.sub_kategori_bahan || ''}
                                  onValueChange={(val) => { handleHutangItemChange(idx, 'kategori', 'Bahan Cair'); handleHutangItemChange(idx, 'sub_kategori_bahan', val); }}
                                >
                                  <SelectTrigger className="h-9"><SelectValue placeholder="-- Sub Kategori --" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Bibit">Bibit</SelectItem>
                                    <SelectItem value="Alkohol">Alkohol</SelectItem>
                                    <SelectItem value="Aqua Des">Aqua Des</SelectItem>
                                    <SelectItem value="DPG">DPG</SelectItem>
                                    <SelectItem value="Peg">Peg</SelectItem>
                                    <SelectItem value="Sustain">Sustain</SelectItem>
                                    <SelectItem value="Lainnya">Lainnya</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : htItem.kategori_utama === 'Item Tambahan' ? (
                                <Select 
                                  value={htItem.sub_kategori_tambahan || ''}
                                  onValueChange={(val) => { handleHutangItemChange(idx, 'kategori', 'Item Tambahan'); handleHutangItemChange(idx, 'sub_kategori_tambahan', val); }}
                                >
                                  <SelectTrigger className="h-9"><SelectValue placeholder="-- Sub Kategori --" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Stiker">Stiker</SelectItem>
                                    <SelectItem value="Box">Box</SelectItem>
                                    <SelectItem value="Sablon">Sablon</SelectItem>
                                    <SelectItem value="Hot Print">Hot Print</SelectItem>
                                    <SelectItem value="Gift Card">Gift Card</SelectItem>
                                    <SelectItem value="Coloring">Coloring</SelectItem>
                                    <SelectItem value="Lainnya">Lainnya</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Select disabled>
                                  <SelectTrigger className="h-9"><SelectValue placeholder="Pilih kategori dulu" /></SelectTrigger>
                                  <SelectContent><SelectItem value="-">-</SelectItem></SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs font-bold text-slate-600">Item Inventori (Opsional)</Label>
                            <Select 
                              disabled={!htItem.kategori && !htItem.sub_kategori_bahan && !htItem.sub_kategori_tambahan}
                              value={htItem.item_id_raw || ''}
                              onValueChange={(val) => {
                                if (!val) return;
                                const [type, id] = val.split('_id_');
                                const invItem = allInventoryItems.find(i => String(i.id) === id && i._type === type);
                                if (invItem) {
                                  const newItems = [...hutangForm.items];
                                  newItems[idx] = { ...newItems[idx], nama_item: invItem.nama, harga: invItem.harga_rupiah || invItem.harga_beli || '', item_id_raw: val };
                                  setHutangForm({ ...hutangForm, items: newItems });
                                }
                              }}
                            >
                              <SelectTrigger className="h-9"><SelectValue placeholder="-- Pilih item --" /></SelectTrigger>
                              <SelectContent className="max-h-[200px]">
                                {allInventoryItems
                                  .filter(invItem => {
                                    if (htItem.kategori_utama === 'Packaging') return invItem._type === htItem.kategori;
                                    if (htItem.kategori_utama === 'Bahan Cair') return invItem._type === 'Bahan Cair' && (!htItem.sub_kategori_bahan || invItem.kategori === htItem.sub_kategori_bahan);
                                    if (htItem.kategori_utama === 'Item Tambahan') return invItem._type === 'Item Tambahan' && (!htItem.sub_kategori_tambahan || invItem.kategori === htItem.sub_kategori_tambahan);
                                    return false;
                                  })
                                  .map(invItem => (
                                  <SelectItem key={`${invItem._type}_id_${invItem.id}`} value={`${invItem._type}_id_${invItem.id}`}>
                                    {invItem.nama}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs font-bold text-slate-600">Nama Item <span className="text-rose-500">*</span></Label>
                            <Input 
                              value={htItem.nama_item} 
                              onChange={e => handleHutangItemChange(idx, 'nama_item', e.target.value)} 
                              placeholder="Contoh: Alkohol 96%"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-slate-600">Qty / Pcs <span className="text-rose-500">*</span></Label>
                              <Input 
                                type="number"
                                value={htItem.qty} 
                                onChange={e => handleHutangItemChange(idx, 'qty', e.target.value)} 
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-slate-600">Harga Satuan <span className="text-rose-500">*</span></Label>
                              <Input 
                                type="number"
                                value={htItem.harga} 
                                onChange={e => handleHutangItemChange(idx, 'harga', e.target.value)} 
                              />
                            </div>
                          </div>

                          {itemTotal > 0 && (
                            <div className="bg-rose-50 p-2 rounded-lg flex justify-between items-center">
                              <span className="text-[11px] font-bold text-slate-500">Subtotal Item</span>
                              <span className="font-bold text-rose-600 text-sm">Rp {fmt(itemTotal)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                <button
                  type="button"
                  onClick={handleAddHutangItem}
                  className="w-full py-2.5 rounded-xl border-2 border-dashed border-slate-300 text-sm font-semibold text-slate-500 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Tambah Item Lain
                </button>
              </div>

              {/* Grand total */}
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 flex justify-between items-center">
                <span className="text-xs font-bold text-slate-500">Total Biaya Hutang ({hutangForm.items.length} item)</span>
                <span className="font-black text-rose-600">
                  Rp {fmt(hutangForm.items.reduce((s, i) => s + (Number(i.qty) || 0) * (Number(i.harga) || 0), 0))}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-slate-500">Tgl Barang Sampai</Label>
                  <CustomDatePicker
                    value={hutangForm.tanggal_sampai} 
                    onChange={date => setHutangForm({ ...hutangForm, tanggal_sampai: date })} 
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-bold uppercase text-slate-500">Jatuh Tempo <span className="text-rose-500">*</span></Label>
                  <CustomDatePicker
                    value={hutangForm.jatuh_tempo} 
                    onChange={date => setHutangForm({ ...hutangForm, jatuh_tempo: date })} 
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="w-full" onClick={() => setShowHutangModal(false)}>
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  disabled={createHutangMut?.isPending || updateHutangMut?.isPending || createHutangMut?.isLoading || updateHutangMut?.isLoading}
                  className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                >
                  {editHutangId ? 'Update Hutang' : 'Simpan Hutang'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL EDIT PO TEMPO */}
      {editPoTempoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 ">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-4 border-b border-slate-100 bg-slate-50/50">
              <h2 className="font-bold text-slate-800 text-sm">Edit Tanggal Tempo Invoice</h2>
              <button 
                onClick={() => setEditPoTempoModal(null)}
                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Invoice</Label>
                <div className="text-sm font-black text-slate-800 px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg">{editPoTempoModal.id}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-slate-600">Tanggal Barang Sampai</Label>
                <CustomDatePicker
                  value={editPoTempoModal.tanggal_barang_sampai || ''} 
                  onChange={date => setEditPoTempoModal({ ...editPoTempoModal, tanggal_barang_sampai: date })} 
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-bold text-slate-600">Tanggal Jatuh Tempo <span className="text-rose-500">*</span></Label>
                <CustomDatePicker
                  value={editPoTempoModal.tanggal_jatuh_tempo || ''} 
                  onChange={date => setEditPoTempoModal({ ...editPoTempoModal, tanggal_jatuh_tempo: date })} 
                  required
                />
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setEditPoTempoModal(null)}>Batal</Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  onClick={() => {
                    if (!editPoTempoModal.tanggal_jatuh_tempo) {
                      toast.error('Tanggal jatuh tempo harus diisi!');
                      return;
                    }
                    base44.entities.ClientPOs.update(editPoTempoModal.id, {
                      tanggal_barang_sampai: editPoTempoModal.tanggal_barang_sampai,
                      tanggal_jatuh_tempo: editPoTempoModal.tanggal_jatuh_tempo
                    }).then(() => {
                      toast.success('Tanggal tempo berhasil diupdate!');
                      setEditPoTempoModal(null);
                      setTimeout(() => window.location.reload(), 500);
                    }).catch(err => {
                      toast.error('Gagal update tempo');
                      console.error(err);
                    });
                  }}
                >
                  Simpan Tanggal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingPrintClient && (
        <div className="fixed inset-0 bg-black/60  z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-[#8C6208]/20">
            {/* Header Asa */}
            <div className="bg-gradient-to-br from-[#FDFBF7] to-[#F3EAD3] p-6 text-center relative overflow-hidden border-b border-[#8C6208]/10">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 mx-auto bg-white rounded-full flex items-center justify-center mb-3 shadow-md p-0.5 border-2 border-[#D4AF37]">
                  <img src="/asa-profile.png" alt="Asa Profile" className="w-full h-full rounded-full object-cover" onError={(e) => { e.target.src = '/logo.png'; }} />
                </div>
                <h3 className="text-xl font-black text-[#8C6208] tracking-tight">Halo {(() => {
                  try {
                    const sbKeys = Object.keys(localStorage).filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
                    if (sbKeys.length > 0) {
                      const auth = JSON.parse(localStorage.getItem(sbKeys[0]));
                      return auth?.user?.user_metadata?.full_name?.split(' ')[0] || 'Tim Satala';
                    }
                  } catch (e) {}
                  return 'Tim Satala';
                })()}! ✨</h3>
                <p className="text-[#8C6208]/70 text-sm mt-1 font-bold">Asa butuh konfirmasi kamu nih...</p>
              </div>
            </div>
            
            {/* Body */}
            <div className="p-6 space-y-6">
              <p className="text-slate-600 text-[15px] leading-relaxed text-center font-medium">
                Rekap tagihan buat <span className="font-bold text-[#8C6208] uppercase px-1">{pendingPrintClient.clientName.split(' / ')[0]}</span> ini mau dicetak pakai rincian produk (nama barang & qty) atau cukup total global per invoice aja?
              </p>
              
              <div className="grid grid-cols-1 gap-3">
                <Button 
                  className="w-full h-12 bg-[#F9F5EC] hover:bg-[#F3EAD3] text-[#8C6208] border border-[#8C6208]/20 justify-start px-4 font-bold shadow-sm transition-all hover:scale-[1.02]"
                  onClick={() => {
                    executeExportPDF(pendingPrintClient, true);
                    setPendingPrintClient(null);
                  }}
                >
                  <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center mr-3 shadow-sm border border-[#8C6208]/10">
                    <FileText className="w-4 h-4 text-[#8C6208]" />
                  </div>
                  Pakai Rincian (Detail)
                </Button>
                
                <Button 
                  variant="outline"
                  className="w-full h-12 justify-start px-4 font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-50 transition-all hover:scale-[1.02]"
                  onClick={() => {
                    executeExportPDF(pendingPrintClient, false);
                    setPendingPrintClient(null);
                  }}
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center mr-3">
                    <FileText className="w-4 h-4 text-slate-500" />
                  </div>
                  Total Global Aja
                </Button>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 p-4 border-t border-slate-100 flex justify-center">
              <Button 
                variant="ghost" 
                className="text-slate-400 hover:text-rose-500 hover:bg-rose-50 text-xs font-bold"
                onClick={() => setPendingPrintClient(null)}
              >
                Gak jadi deh
              </Button>
            </div>
          </div>
        </div>
      )}

      </div>
      <BillingStatementPrint data={printData} />
    </div>
  );
}
